import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Careers: React.FC = () => {
  const { t } = useTranslation();

  const jobOpenings = [
    {
      id: 1,
      title: 'Senior AI Engineer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead the development of our AI-powered document processing algorithms and machine learning models.',
      requirements: [
        '5+ years experience in machine learning and AI',
        'Strong background in NLP and computer vision',
        'Experience with Python, TensorFlow, PyTorch',
        'PhD in Computer Science or related field preferred'
      ]
    },
    {
      id: 2,
      title: 'Full Stack Developer',
      department: 'Engineering',
      location: 'Warsaw, Poland',
      type: 'Full-time',
      description: 'Build scalable web applications and APIs that power our AI accounting platform.',
      requirements: [
        '3+ years experience with React and Node.js',
        'Experience with cloud platforms (AWS, GCP)',
        'Knowledge of TypeScript and modern web technologies',
        'Experience with microservices architecture'
      ]
    },
    {
      id: 3,
      title: 'Product Manager',
      department: 'Product',
      location: 'Remote',
      type: 'Full-time',
      description: 'Drive product strategy and roadmap for our AI-powered accounting solutions.',
      requirements: [
        '4+ years product management experience',
        'Background in B2B SaaS products',
        'Experience with financial or accounting software',
        'Strong analytical and communication skills'
      ]
    },
    {
      id: 4,
      title: 'Sales Engineer',
      department: 'Sales',
      location: 'Warsaw, Poland',
      type: 'Full-time',
      description: 'Help prospects understand our AI solutions and drive technical sales conversations.',
      requirements: [
        '3+ years in technical sales or sales engineering',
        'Background in accounting or finance software',
        'Strong presentation and demo skills',
        'Fluent in Polish and English'
      ]
    }
  ];

  const benefits = [
    {
      icon: '💰',
      title: t('careers.whyWorkWithUs.benefits.competitiveSalary.title'),
      description: t('careers.whyWorkWithUs.benefits.competitiveSalary.description')
    },
    {
      icon: '🏠',
      title: t('careers.whyWorkWithUs.benefits.remoteWork.title'),
      description: t('careers.whyWorkWithUs.benefits.remoteWork.description')
    },
    {
      icon: '📚',
      title: t('careers.whyWorkWithUs.benefits.learningBudget.title'),
      description: t('careers.whyWorkWithUs.benefits.learningBudget.description')
    },
    {
      icon: '🏥',
      title: t('careers.whyWorkWithUs.benefits.healthWellness.title'),
      description: t('careers.whyWorkWithUs.benefits.healthWellness.description')
    },
    {
      icon: '⚡',
      title: t('careers.whyWorkWithUs.benefits.cuttingEdgeTech.title'),
      description: t('careers.whyWorkWithUs.benefits.cuttingEdgeTech.description')
    },
    {
      icon: '🎯',
      title: t('careers.whyWorkWithUs.benefits.impact.title'),
      description: t('careers.whyWorkWithUs.benefits.impact.description')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white overflow-hidden relative">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-32 right-16 w-96 h-96 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 via-cyan-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-2xl group-hover:shadow-blue-500/25 transition-all duration-300 group-hover:scale-105">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <span className="text-3xl font-black bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent tracking-tight">Alenis</span>
            </Link>
            <div className="flex items-center space-x-6">
              <LanguageSwitcher />
              <Link to="/" className="px-6 py-3 text-white/80 hover:text-white transition-all duration-300 hover:bg-white/10 rounded-xl backdrop-blur-sm">
                {t('navigation.home')}
              </Link>
              <Link to="/about" className="px-6 py-3 text-white/80 hover:text-white transition-all duration-300 hover:bg-white/10 rounded-xl backdrop-blur-sm">
                {t('about.footer.aboutUs')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-8xl font-black mb-8 leading-tight">
            <span className="block bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent tracking-tight">
              {t('careers.title')}
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            {t('careers.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/contact"
              className="px-10 py-5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xl font-bold rounded-2xl hover:from-blue-700 hover:to-cyan-700 transition-all shadow-2xl hover:shadow-blue-500/25 transform hover:scale-105"
            >
              {t('careers.applyNow')}
            </Link>
            <a
              href="mailto:careers@alenis.services"
              className="px-10 py-5 bg-white/10 border-2 border-white/20 text-white text-xl font-bold rounded-2xl hover:bg-white/20 transition-all backdrop-blur-sm"
            >
              {t('careers.sendResume')}
            </a>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">{t('careers.whyWorkWithUs.title')}</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              {t('careers.whyWorkWithUs.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-slate-300 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="relative z-10 py-20 bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">{t('careers.openPositions.title')}</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              {t('careers.openPositions.subtitle')}
            </p>
          </div>
          
          {/* Not Hiring Message */}
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-12 border border-white/10 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-8">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-3xl font-bold text-white mb-6">{t('careers.openPositions.notHiring.title')}</h3>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('careers.openPositions.notHiring.description')}
            </p>
            <p className="text-lg text-slate-400 mb-8">
              {t('careers.openPositions.notHiring.subdescription')}
            </p>
            <a
              href="mailto:careers@alenis.services"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-lg font-bold rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all shadow-xl hover:shadow-2xl hover:shadow-blue-500/25 transform hover:scale-105"
            >
              {t('careers.openPositions.notHiring.sendResume')}
            </a>
          </div>

          {/* Commented out job openings for future use */}
          {/* 
          <div className="space-y-8">
            {jobOpenings.map((job) => (
              <div key={job.id} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-slate-300">
                      <span className="flex items-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span>{job.department}</span>
                      </span>
                      <span className="flex items-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{job.location}</span>
                      </span>
                      <span className="flex items-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{job.type}</span>
                      </span>
                    </div>
                  </div>
                  <button className="mt-4 lg:mt-0 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all">
                    Apply Now
                  </button>
                </div>
                
                <p className="text-slate-300 leading-relaxed mb-6">{job.description}</p>
                
                <div>
                  <h4 className="text-lg font-bold text-white mb-3">Requirements:</h4>
                  <ul className="space-y-2">
                    {job.requirements.map((req, index) => (
                      <li key={index} className="flex items-start space-x-3 text-slate-300">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          */}
        </div>
      </section>

      {/* Culture Section */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">{t('careers.culture.title')}</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              {t('careers.culture.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-white mb-8">{t('careers.culture.innovationFirst.title')}</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">{t('careers.culture.innovationFirst.cuttingEdgeTech.title')}</h4>
                    <p className="text-slate-300">{t('careers.culture.innovationFirst.cuttingEdgeTech.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">{t('careers.culture.innovationFirst.collaborativeEnv.title')}</h4>
                    <p className="text-slate-300">{t('careers.culture.innovationFirst.collaborativeEnv.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">{t('careers.culture.innovationFirst.fastPacedGrowth.title')}</h4>
                    <p className="text-slate-300">{t('careers.culture.innovationFirst.fastPacedGrowth.description')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-400/30 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">{t('careers.culture.perksBenefits.title')}</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-slate-300">{t('careers.culture.perksBenefits.flexibleHours')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-slate-300">{t('careers.culture.perksBenefits.stockOptions')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-slate-300">{t('careers.culture.perksBenefits.teamRetreats')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-slate-300">{t('careers.culture.perksBenefits.professionalDev')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-slate-300">{t('careers.culture.perksBenefits.topTierEquipment')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-slate-300">{t('careers.culture.perksBenefits.unlimitedVacation')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">{t('careers.cta.title')}</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
            {t('careers.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="mailto:careers@alenis.services"
              className="px-10 py-5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xl font-bold rounded-2xl hover:from-blue-700 hover:to-cyan-700 transition-all shadow-2xl hover:shadow-blue-500/25 transform hover:scale-105"
            >
              {t('careers.cta.sendResume')}
            </a>
            <Link
              to="/contact"
              className="px-10 py-5 bg-white/10 border-2 border-white/20 text-white text-xl font-bold rounded-2xl hover:bg-white/20 transition-all backdrop-blur-sm"
            >
              {t('careers.cta.getInTouch')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-slate-900/80 backdrop-blur-sm border-t border-white/10 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">A</span>
                </div>
                <span className="text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Alenis</span>
              </div>
              <p className="text-slate-400 text-lg max-w-md leading-relaxed">
                Revolutionizing financial management through artificial intelligence and advanced accounting process automation.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold text-lg mb-4">Company</h4>
              <ul className="space-y-3 text-slate-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold text-lg mb-4">Product</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">AI Automation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">PDF Analysis</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Reporting</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center">
            <div className="text-slate-400 text-sm">
              © 2025 Alenis. All rights reserved. Powered by AI.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Careers;
